import { useCallback, useEffect, useState } from "react";
import { ApiError, get } from "../api";

/**
 * One GET, owned by one section of the overview.
 *
 * `data` is `undefined` while loading and `null` when the server answered
 * 404 -- a customer with no report yet is a state to draw, not an error.
 * Each section fetches for itself so that one failing source (metrics read
 * a mirror that may be empty) cannot blank the others.
 */
export function useFetch<T>(url: string) {
  const [data, setData] = useState<T | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    void get<T>(url)
      .then((d) => live && setData(d))
      .catch((e) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 404) setData(null);
        else setError((e as Error).message);
      });
    return () => {
      live = false;
    };
  }, [url, tick]);

  // A new URL is a different customer: nothing on screen belongs to them.
  useEffect(() => setData(undefined), [url]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, reload };
}
